import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@hooks/app/modal.hook'

import { Input } from '@components/Input'
import { Modal } from '@components/Modal'
import { UploadPicture } from '@components/UploadPicture'

import { useCreateProject } from '@hooks/project/create-project.hook'

import { LocalFile, ProjectContact } from '@types'

import { Container, Section, Title } from './add.styled'
import { ContactList } from '../../components/ContactList'
import { v4 } from 'uuid'

export const AddPage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const [projectName, setProjectName] = useState<string>('')
  const [companyName, setCompanyName] = useState<string>('')

  const [photos, setPhotos] = useState<LocalFile[]>([])
  const [contacts, setContacts] = useState<ProjectContact[]>([])

  const { createProject } = useCreateProject()

  const onAddContact = () => {
    const contact: ProjectContact = {
      id: v4(),
      role: '',
      companyName: '',
      order: contacts.length,
    }

    setContacts([...contacts, contact])
  }

  const onRemoveContact = (id: string) => {
    const newContacts = contacts.filter((contact) => contact.id !== id)

    setContacts(newContacts)
  }

  const onUpdateContact = (id: string, data: Partial<ProjectContact>) => {
    const newContacts = contacts.map((contact) => {
      if (contact.id === id) {
        return {
          ...contact,
          ...data,
        }
      }
      return contact
    })

    setContacts(newContacts)
  }

  const onMoveContactUp = (id: string) => {
    const contactIndex = contacts.findIndex((c) => c.id === id)
    if (contactIndex <= 0) return

    const newContacts = [...contacts]
    const temp = newContacts[contactIndex]

    newContacts[contactIndex] = newContacts[contactIndex - 1]
    newContacts[contactIndex - 1] = temp

    newContacts.forEach((contact, index) => {
      contact.order = index
    })

    setContacts(newContacts)
  }

  const onMoveContactDown = (id: string) => {
    const contactIndex = contacts.findIndex((c) => c.id === id)
    if (contactIndex === -1 || contactIndex >= contacts.length - 1) return

    const newContacts = [...contacts]
    const temp = newContacts[contactIndex]

    newContacts[contactIndex] = newContacts[contactIndex + 1]
    newContacts[contactIndex + 1] = temp

    newContacts.forEach((contact, index) => {
      contact.order = index
    })

    setContacts(newContacts)
  }

  return (
    <Modal
      title={t('modules_project_pages_add_title')}
      onSubmitLabel={t('modules_project_pages_add_button_add-project')}
      onClose={() => {
        hideModal('add_project')
      }}
      onSubmit={() => {
        createProject(
          {
            name: projectName,
            companyName,
            contacts,
          },
          photos,
          [],
        )

        hideModal('add_project')
      }}
    >
      <Container>
        <Section>
          <Title>{t('modules_project_pages_add_project_label_section_project')}</Title>
          <Input
            autoFocus
            required
            label={t('modules_project_pages_add_project_label_project-name')}
            placeholder={t('modules_project_pages_add_project_placeholder_project-name')}
            value={projectName}
            onChange={(v) => setProjectName(v)}
          />
          <Input
            required
            label={t('modules_project_pages_add_project_label_company_name')}
            placeholder={t('modules_project_pages_add_project_placeholder_company_name')}
            value={companyName}
            onChange={(v) => setCompanyName(v)}
          />

          <UploadPicture
            disabled={photos.length > 0}
            label={t('modules_project_pages_add_project_label_project_photo')}
            files={photos}
            setFiles={(files) => setPhotos(() => [...files] as LocalFile[])}
          />
        </Section>

        <Section>
          <Title>{t('modules_project_pages_add_project_label_section_actors')}</Title>
          <ContactList
            contacts={contacts}
            onAdd={onAddContact}
            onRemove={onRemoveContact}
            onUpdate={onUpdateContact}
            onMoveUp={onMoveContactUp}
            onMoveDown={onMoveContactDown}
          />
        </Section>
      </Container>
    </Modal>
  )
}
